import { mdiClose, mdiWeb } from '@mdi/js';
import Icon from '@mdi/react';
import { useLocation } from '@reach/router';
import { StaticImage } from 'gatsby-plugin-image';
import { useI18next } from 'gatsby-plugin-react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { CMS_THEME } from '../../common/enums';
import { ButtonLink } from '../button/Button';
import { LanguageSelectorContainer, StyledPopover, UnstyledButton } from './style';

interface LanguageSelectorProps {
  language: Languages;
  shouldDisplay?: boolean;
}

const languageLabel: Record<string, string> = {
  en: 'English',
  fr: 'Français',
};

const LanguageSelector = ({ language, shouldDisplay = true }: LanguageSelectorProps) => {
  const { languages } = useI18next();
  const [show, setShow] = useState<boolean>(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const handleClose = () => {
    setShow(false);

    // Return focus to trigger when closed
    setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
  };

  const handleShow = () => setShow(true);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleShow();
    }
  };

  // Focus management for when popover opens
  useEffect(() => {
    if (show && closeButtonRef.current) {
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    }
  }, [show]);

  // Handle keyboard navigation within the popover
  const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
      return;
    }

    if (e.key === 'Tab') {
      const popover = e.currentTarget;
      const focusableElements = popover.querySelectorAll(
        'button, [href]:not(.d-none), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements.length === 0) return;
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  const location = useLocation();

  const frenchLangcodeFragmentRegex = /^\/(fr)\/?/;

  const path =
    language === 'fr'
      ? location.pathname.replace(frenchLangcodeFragmentRegex, '/')
      : location.pathname;

  const cleanedPathName = path + location.search;

  if (!shouldDisplay) return null;

  return (
    <OverlayTrigger
      trigger="click"
      placement="bottom"
      show={show}
      onToggle={setShow}
      overlay={
        <StyledPopover ref={popoverRef} onKeyDown={handlePopoverKeyDown}>
          <Popover.Body style={{ padding: '0' }}>
            <LanguageSelectorContainer>
              <div className="header">
                <div style={{ width: '100px' }}>
                  <StaticImage
                    src="../../assets/media/images/logo.png"
                    alt="logo"
                    className="mt-3"
                  />
                </div>
                <UnstyledButton
                  ref={closeButtonRef}
                  onClick={handleClose}
                  aria-label="Close language selector"
                >
                  <Icon path={mdiClose} size={1} />
                </UnstyledButton>
              </div>
              <div className="content">
                <h6>
                  <Icon path={mdiWeb} size={1} /> Select Language
                </h6>
                {languages.map((lang, index) => (
                  <ButtonLink
                    key={index}
                    cmsTheme={CMS_THEME.LIGHT}
                    to={lang === 'en' ? cleanedPathName : `/${lang}${cleanedPathName}`}
                    shape="smallRadius"
                    variant="btn-outline"
                    onClick={handleClose}
                  >
                    {languageLabel[lang]}
                  </ButtonLink>
                ))}
              </div>
            </LanguageSelectorContainer>
          </Popover.Body>
        </StyledPopover>
      }
    >
      <Button
        ref={triggerRef}
        onClick={handleShow}
        onKeyDown={handleKeyDown}
        variant="link"
        className="p-0 p-xxl-4 border-0 d-flex align-items-center"
        aria-expanded={show}
        aria-haspopup="dialog"
        aria-label="Select language"
        style={{ textDecoration: 'none', color: '#2B2B2B', fontSize: '16px', fontWeight: '400' }}
      >
        {/* Full button for extra extra large screens */}
        <div className="d-none d-xxl-flex align-items-center">
          <span className="d-sm-none d-lg-inline-block me-1" style={{ color: '#2B2B2B' }}>
            {languageLabel[language]}
          </span>

          <Icon path={mdiWeb} size={1} style={{ color: '#2B2B2B' }} />
        </div>
        {/* Circular button for small screens */}
        <div
          className="rounded-pill bg-light p-2 d-xxl-none ms-2 justify-content-center"
          style={{
            height: '48px',
            width: '48px',
            color: '#2B2B2B',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Icon path={mdiWeb} size={1} />
        </div>
        {/* Hidden links for SEO */}
        {languages.map((lang, index) => (
          <a
            key={index}
            className="d-none"
            href={lang === 'en' ? cleanedPathName : `/${lang}${cleanedPathName}`}
            aria-hidden="true"
            tabIndex={-1}
          >
            {languageLabel[lang]}
          </a>
        ))}
      </Button>
    </OverlayTrigger>
  );
};

export default LanguageSelector;
